import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import type { TFunction } from "i18next";
import type { SavingInstitution } from "@/hooks/useSavingPockets";
import { EmptyState, MessageBanner, StatTile } from "@/components/ui/primitives";
import { EntityCard } from "@/components/ui/EntityCard";

export function InstitutionCard({
  institution,
  total,
  progress,
  onEdit,
  onDelete,
  bordered,
  isSwiped,
  onRowClick,
  t,
  onPointerStart,
  onPointerMove,
  onPointerEnd,
  onActionToggle,
}: {
  institution: SavingInstitution;
  total: string;
  progress: number;
  onEdit: () => void;
  onDelete: () => void;
  bordered: boolean;
  isSwiped: boolean;
  onRowClick: () => void;
  t: TFunction;
  onPointerStart: (event: ReactPointerEvent<HTMLButtonElement>, id: number | string, group: "institution" | "pocket") => void;
  onPointerMove: (event: ReactPointerEvent<HTMLButtonElement>, id: number | string, group: "institution" | "pocket") => void;
  onPointerEnd: () => void;
  onActionToggle: (event: ReactMouseEvent<HTMLSpanElement>, id: number | string, group: "institution" | "pocket") => void;
}) {
  return (
    <div className={`py-5 ${bordered ? "border-b border-[color:color-mix(in_srgb,var(--app-border)_72%,transparent)]" : ""}`}>
      <EntityCard
        title={institution.name}
        subtitle={institution.description || undefined}
        value={total}
        progress={{
          percent: progress,
          ariaLabel: `${t("savingPockets.progress", { defaultValue: "Progress" })}: ${Math.round(progress)}%`,
        }}
        actions={[
          {
            kind: "edit",
            label: `${t("common:actions.edit")} ${institution.name}`,
            onClick: onEdit,
          },
          {
            kind: "delete",
            label: `${t("common:actions.delete")} ${institution.name}`,
            onClick: onDelete,
          },
        ]}
        isSwiped={isSwiped}
        toggleLabel={t("savingPockets.showActions", { defaultValue: "Mostrar acciones" })}
        onClick={onRowClick}
        onPointerDown={(event) => onPointerStart(event, institution.id, "institution")}
        onPointerMove={(event) => onPointerMove(event, institution.id, "institution")}
        onPointerUp={onPointerEnd}
        onPointerLeave={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onToggle={(event) => onActionToggle(event, institution.id, "institution")}
      />
    </div>
  );
}

export function PocketCard({
  rowId,
  title,
  description,
  amount,
  annualRate,
  freezeReturn,
  finalAmount,
  monthlyReturn,
  yearlyReturn,
  freezeDate,
  freezeDateLabel,
  freezeTime,
  freezeTimeLabel,
  unfreezeDate,
  unfreezeDateLabel,
  pocketCount,
  pocketCountLabel,
  onEdit,
  onDelete,
  t,
  bordered,
  isSwiped,
  onRowClick,
  onPointerStart,
  onPointerMove,
  onPointerEnd,
  onActionToggle,
}: {
  rowId: number | string;
  title: string;
  description: string;
  amount: string;
  annualRate: string;
  freezeReturn: string | null;
  finalAmount: string;
  monthlyReturn: string;
  yearlyReturn: string;
  freezeDate: string;
  freezeDateLabel: string;
  freezeTime: string;
  freezeTimeLabel: string;
  unfreezeDate: string;
  unfreezeDateLabel: string;
  pocketCount: number | null;
  pocketCountLabel: string;
  onEdit?: () => void;
  onDelete?: () => void;
  t: TFunction;
  bordered: boolean;
  isSwiped: boolean;
  onRowClick?: () => void;
  onPointerStart: (event: ReactPointerEvent<HTMLButtonElement>, id: number | string, group: "institution" | "pocket") => void;
  onPointerMove: (event: ReactPointerEvent<HTMLButtonElement>, id: number | string, group: "institution" | "pocket") => void;
  onPointerEnd: () => void;
  onActionToggle: (event: ReactMouseEvent<HTMLSpanElement>, id: number | string, group: "institution" | "pocket") => void;
}) {
  return (
    <article className={`h-full ${bordered ? "border-b border-[color:color-mix(in_srgb,var(--app-border)_72%,transparent)] pb-5" : ""}`}>
      <EntityCard
        title={title}
        subtitle={description || undefined}
        meta={pocketCount != null ? [<span key="pocket-count">{pocketCount} {pocketCountLabel}</span>] : undefined}
        value={amount}
        valueCaption={annualRate}
        description={
          <div className="grid gap-2 sm:grid-cols-2">
            <FeatureStat label={t("savingPockets.finalAmount")} value={finalAmount} primary />
            <FeatureStat label={t("savingPockets.monthlyReturn")} value={monthlyReturn} />
            <FeatureStat label={t("savingPockets.yearlyReturn")} value={yearlyReturn} />
            {freezeReturn ? <FeatureStat label={t("savingPockets.freezeReturn")} value={freezeReturn} /> : null}
            <FeatureStat label={freezeDateLabel} value={freezeDate} subtle />
            <FeatureStat label={freezeTimeLabel} value={freezeTime} subtle />
            <FeatureStat label={unfreezeDateLabel} value={unfreezeDate} subtle />
          </div>
        }
        actions={
          onEdit && onDelete
            ? [
                {
                  kind: "edit",
                  label: `${t("common:actions.edit")} ${title}`,
                  onClick: onEdit,
                },
                {
                  kind: "delete",
                  label: `${t("common:actions.delete")} ${title}`,
                  onClick: onDelete,
                },
              ]
            : []
        }
        isSwiped={isSwiped}
        toggleLabel={t("savingPockets.showActions", { defaultValue: "Mostrar acciones" })}
        onClick={onRowClick ?? (() => undefined)}
        onPointerDown={(event) => onPointerStart(event, rowId, "pocket")}
        onPointerMove={(event) => onPointerMove(event, rowId, "pocket")}
        onPointerUp={onPointerEnd}
        onPointerLeave={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onToggle={onEdit && onDelete ? (event) => onActionToggle(event, rowId, "pocket") : undefined}
      />
    </article>
  );
}

function FeatureStat({ label, value, primary = false, subtle = false }: { label: string; value: string; primary?: boolean; subtle?: boolean }) {
  return <StatTile label={label} value={value} tone={primary ? "primary" : subtle ? "subtle" : "default"} />;
}

export function EmptyStateCard({ title, body, large = false }: { title: string; body: string; large?: boolean }) {
  return <EmptyState title={title} body={body} className={large ? "px-6 py-12" : "px-5 py-8"} />;
}

export function MessageCard({ children, tone = "default" }: { children: ReactNode; tone?: "default" | "error" }) {
  return <MessageBanner tone={tone} className="mb-6">{children}</MessageBanner>;
}
